import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useState } from 'react';
import styled from 'styled-components';
import { Plus, ExternalLink, Play, Trash2, Heart, CheckCircle, XCircle, AlertCircle, Search } from 'lucide-react';
import { theme } from '@styles/theme';
import { Button, Card, CardContent, DeploymentStatus, Input, Modal, StatusBadge, Spinner, getPrototypeStatusType, } from '@components/index';
import { usePrototypes, usePrototypeDeployments } from '@hooks/index';
import { prototypesApi } from '@infrastructure/api/prototypesApi';
const Container = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.lg};
`;
const Header = styled.div `
  display: flex;
  align-items: center;
  justify-content: space-between;
`;
const Title = styled.h1 `
  font-size: ${theme.typography.fontSize['2xl']};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
`;
const PrototypeGrid = styled.div `
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: ${theme.spacing.md};
`;
const PrototypeCard = styled(Card) `
  display: flex;
  flex-direction: column;
`;
const PrototypeHeader = styled.div `
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: ${theme.spacing.sm};
`;
const PrototypeName = styled.h3 `
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.primary};
  margin: 0;
`;
const PrototypeDescription = styled.p `
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin: ${theme.spacing.sm} 0 0;
  line-height: 1.5;
`;
const PrototypeUrl = styled.a `
  display: flex;
  align-items: center;
  gap: ${theme.spacing.xs};
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.muted};
  text-decoration: none;
  margin-top: ${theme.spacing.sm};
  word-break: break-all;

  &:hover {
    color: ${theme.colors.text.secondary};
  }
`;
const PrototypeActions = styled.div `
  display: flex;
  gap: ${theme.spacing.sm};
  margin-top: auto;
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border.primary};
`;
const ViewLink = styled.a `
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: ${theme.spacing.xs};
  padding: ${theme.spacing.xs} ${theme.spacing.sm};
  font-size: ${theme.typography.fontSize.sm};
  font-weight: ${theme.typography.fontWeight.medium};
  border-radius: ${theme.borderRadius.md};
  background-color: transparent;
  color: ${theme.colors.text.primary};
  border: 1px solid ${theme.colors.border.primary};
  text-decoration: none;
  transition: all ${theme.transitions.fast};

  &:hover {
    background-color: ${theme.colors.background.tertiary};
    border-color: ${theme.colors.border.secondary};
  }
`;
const Form = styled.form `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;
const LoadingContainer = styled.div `
  display: flex;
  justify-content: center;
  padding: ${theme.spacing.xl};
`;
const EmptyState = styled.div `
  text-align: center;
  padding: ${theme.spacing.xxl};
  color: ${theme.colors.text.muted};
`;
const EmptyStateTitle = styled.h3 `
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${theme.colors.text.secondary};
  margin-bottom: ${theme.spacing.sm};
`;
const EmptyStateDescription = styled.p `
  font-size: ${theme.typography.fontSize.sm};
  margin-bottom: ${theme.spacing.lg};
`;
const ErrorMessage = styled.div `
  color: ${theme.colors.status.error};
  font-size: ${theme.typography.fontSize.sm};
  padding: ${theme.spacing.sm};
  background-color: ${theme.colors.status.error}20;
  border-radius: ${theme.borderRadius.md};
`;
const BadgesContainer = styled.div `
  display: flex;
  gap: ${theme.spacing.xs};
  align-items: center;
  flex-wrap: wrap;
`;
const HealthIndicator = styled.div `
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: ${theme.typography.fontSize.xs};
  color: ${({ $status }) => {
    switch ($status) {
        case 'healthy':
            return theme.colors.status.success;
        case 'unhealthy':
            return theme.colors.status.error;
        default:
            return theme.colors.text.muted;
    }
}};
`;
const ValidationContainer = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.md};
`;
const ValidationHeader = styled.div `
  display: flex;
  align-items: center;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.md};
  border-radius: ${theme.borderRadius.md};
  background-color: ${({ $isValid }) => {
    if ($isValid === null)
        return theme.colors.background.secondary;
    return $isValid ? `${theme.colors.status.success}15` : `${theme.colors.status.error}15`;
}};
  border: 1px solid ${({ $isValid }) => {
    if ($isValid === null)
        return theme.colors.border.primary;
    return $isValid ? theme.colors.status.success : theme.colors.status.error;
}};
`;
const ValidationTitle = styled.span `
  font-weight: ${theme.typography.fontWeight.semibold};
  color: ${({ $isValid }) => {
    if ($isValid === null)
        return theme.colors.text.primary;
    return $isValid ? theme.colors.status.success : theme.colors.status.error;
}};
`;
const ValidationSummary = styled.span `
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  margin-left: auto;
`;
const CheckList = styled.div `
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;
const CheckItem = styled.div `
  display: flex;
  align-items: flex-start;
  gap: ${theme.spacing.sm};
  padding: ${theme.spacing.sm};
  border-radius: ${theme.borderRadius.sm};
  background-color: ${({ $passed }) => $passed ? `${theme.colors.status.success}10` : `${theme.colors.status.error}10`};
`;
const CheckIcon = styled.div `
  flex-shrink: 0;
  color: ${({ $passed }) => $passed ? theme.colors.status.success : theme.colors.status.error};
`;
const CheckContent = styled.div `
  flex: 1;
`;
const CheckName = styled.div `
  font-weight: ${theme.typography.fontWeight.medium};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
`;
const CheckMessage = styled.div `
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;
const CheckDetails = styled.div `
  font-size: ${theme.typography.fontSize.xs};
  color: ${theme.colors.text.muted};
  margin-top: ${theme.spacing.xs};
`;
const UrlInputContainer = styled.div `
  display: flex;
  gap: ${theme.spacing.sm};
  align-items: flex-end;
`;
const UrlInputWrapper = styled.div `
  flex: 1;
`;
const TearDownList = styled.ul `
  margin: ${theme.spacing.sm} 0 ${theme.spacing.md} ${theme.spacing.lg};
  padding-left: 0;
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing.xs};
`;
const TearDownWarning = styled.div `
  background-color: ${theme.colors.status.error}10;
  border: 1px solid ${theme.colors.status.error}40;
  border-radius: ${theme.borderRadius.md};
  padding: ${theme.spacing.sm} ${theme.spacing.md};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.primary};
`;
const ConfirmInputLabel = styled.label `
  display: block;
  margin-top: ${theme.spacing.md};
  font-size: ${theme.typography.fontSize.sm};
  color: ${theme.colors.text.secondary};
`;
const DeploymentProgressContainer = styled.div `
  margin-top: ${theme.spacing.md};
  padding-top: ${theme.spacing.md};
  border-top: 1px solid ${theme.colors.border.primary};
`;
function PrototypeDeploymentProgress({ prototypeId }) {
    const { deployments } = usePrototypeDeployments(prototypeId, {
        limit: 1,
        autoRefresh: true,
        refreshInterval: 5000,
    });
    const latest = deployments[0];
    if (!latest)
        return null;
    return (_jsx(DeploymentProgressContainer, { children: _jsx(DeploymentStatus, { deployment: latest, showSteps: true, size: "sm" }) }));
}
export function AdminPrototypesPage() {
    const { prototypes, isLoading, error, registerPrototype, deployPrototype, deletePrototype } = usePrototypes();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [modalStep, setModalStep] = useState('input');
    const [formName, setFormName] = useState('');
    const [formDescription, setFormDescription] = useState('');
    const [formGithubUrl, setFormGithubUrl] = useState('');
    const [formError, setFormError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isValidating, setIsValidating] = useState(false);
    const [validationResult, setValidationResult] = useState(null);
    const [deployingId, setDeployingId] = useState(null);
    const [deletingId, setDeletingId] = useState(null);
    const [tearDownTarget, setTearDownTarget] = useState(null);
    const [tearDownConfirmText, setTearDownConfirmText] = useState('');
    const [tearDownError, setTearDownError] = useState(null);
    const handleOpenModal = () => {
        setIsModalOpen(true);
        setModalStep('input');
        setFormError(null);
        setValidationResult(null);
    };
    const handleCloseModal = () => {
        setIsModalOpen(false);
        setModalStep('input');
        setFormName('');
        setFormDescription('');
        setFormGithubUrl('');
        setFormError(null);
        setValidationResult(null);
    };
    const handleValidate = async () => {
        if (!formGithubUrl) {
            setFormError('GitHub repository URL is required');
            return;
        }
        setFormError(null);
        setIsValidating(true);
        setModalStep('validating');
        try {
            const result = await prototypesApi.validateRepository(formGithubUrl);
            setValidationResult(result);
            setModalStep('results');
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to validate repository';
            setFormError(message);
            setModalStep('input');
        }
        finally {
            setIsValidating(false);
        }
    };
    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validationResult?.isValid) {
            setFormError('Please validate the repository first');
            return;
        }
        setFormError(null);
        setIsSubmitting(true);
        try {
            await registerPrototype(formName, formGithubUrl, formDescription || undefined);
            handleCloseModal();
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to register prototype';
            setFormError(message);
        }
        finally {
            setIsSubmitting(false);
        }
    };
    const handleBackToInput = () => {
        setModalStep('input');
        setValidationResult(null);
    };
    const handleDeploy = async (prototype) => {
        setDeployingId(prototype.id);
        try {
            await deployPrototype(prototype.id);
        }
        catch {
            // Error handling could be improved with toast notifications
        }
        finally {
            setDeployingId(null);
        }
    };
    const handleOpenTearDown = (prototype) => {
        setTearDownTarget(prototype);
        setTearDownConfirmText('');
        setTearDownError(null);
    };
    const handleCloseTearDown = () => {
        setTearDownTarget(null);
        setTearDownConfirmText('');
        setTearDownError(null);
    };
    const handleConfirmTearDown = async () => {
        if (!tearDownTarget)
            return;
        if (tearDownConfirmText.trim() !== tearDownTarget.name) {
            setTearDownError(`Type "${tearDownTarget.name}" exactly to confirm.`);
            return;
        }
        setDeletingId(tearDownTarget.id);
        setTearDownError(null);
        try {
            await deletePrototype(tearDownTarget.id);
            handleCloseTearDown();
        }
        catch (err) {
            const message = err instanceof Error ? err.message : 'Failed to delete prototype';
            setTearDownError(message);
        }
        finally {
            setDeletingId(null);
        }
    };
    const renderValidationCheck = (check) => (_jsxs(CheckItem, { "$passed": check.passed, children: [_jsx(CheckIcon, { "$passed": check.passed, children: check.passed ? _jsx(CheckCircle, { size: 18 }) : _jsx(XCircle, { size: 18 }) }), _jsxs(CheckContent, { children: [_jsx(CheckName, { children: check.name }), _jsx(CheckMessage, { children: check.message }), check.details && _jsx(CheckDetails, { children: check.details })] })] }, check.name));
    const renderModalContent = () => {
        if (modalStep === 'validating') {
            return (_jsx(LoadingContainer, { children: _jsx(Spinner, { size: "lg" }) }));
        }
        if (modalStep === 'results' && validationResult) {
            return (_jsxs(ValidationContainer, { children: [_jsxs(ValidationHeader, { "$isValid": validationResult.isValid, children: [validationResult.isValid ? (_jsx(CheckCircle, { size: 24 })) : (_jsx(AlertCircle, { size: 24 })), _jsx(ValidationTitle, { "$isValid": validationResult.isValid, children: validationResult.isValid
                                    ? 'Repository meets requirements'
                                    : 'Repository does not meet requirements' }), _jsxs(ValidationSummary, { children: [validationResult.summary.passed, "/", validationResult.summary.total, " checks passed"] })] }), _jsx(CheckList, { children: validationResult.checks.map(renderValidationCheck) }), validationResult.isValid && (_jsxs(Form, { id: "register-form", onSubmit: handleSubmit, children: [formError && _jsx(ErrorMessage, { children: formError }), _jsx(Input, { label: "Name", value: formName, onChange: (e) => setFormName(e.target.value), placeholder: "My Prototype", required: true }), _jsx(Input, { label: "Description", value: formDescription, onChange: (e) => setFormDescription(e.target.value), placeholder: "Brief description of the prototype" })] }))] }));
        }
        return (_jsxs(Form, { id: "validate-form", onSubmit: (e) => { e.preventDefault(); handleValidate(); }, children: [formError && _jsx(ErrorMessage, { children: formError }), _jsx(UrlInputContainer, { children: _jsx(UrlInputWrapper, { children: _jsx(Input, { label: "GitHub Repository URL", value: formGithubUrl, onChange: (e) => setFormGithubUrl(e.target.value), placeholder: "https://github.com/owner/repo", required: true }) }) }), _jsx("p", { style: { fontSize: theme.typography.fontSize.sm, color: theme.colors.text.muted, margin: 0 }, children: "The repository will be validated to ensure it meets the prototype requirements (Dockerfile, port 80, health endpoint, etc.)" })] }));
    };
    const renderModalFooter = () => {
        if (modalStep === 'validating') {
            return (_jsx(Button, { variant: "ghost", onClick: handleCloseModal, children: "Cancel" }));
        }
        if (modalStep === 'results') {
            if (validationResult?.isValid) {
                return (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", onClick: handleBackToInput, children: "Back" }), _jsx(Button, { type: "submit", form: "register-form", loading: isSubmitting, disabled: isSubmitting || !formName, children: "Register Prototype" })] }));
            }
            return (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", onClick: handleCloseModal, children: "Cancel" }), _jsx(Button, { variant: "secondary", onClick: handleBackToInput, children: "Try Different Repository" })] }));
        }
        return (_jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", onClick: handleCloseModal, children: "Cancel" }), _jsxs(Button, { type: "submit", form: "validate-form", loading: isValidating, disabled: isValidating || !formGithubUrl, children: [_jsx(Search, { size: 16 }), "Validate Repository"] })] }));
    };
    if (isLoading) {
        return (_jsx(LoadingContainer, { children: _jsx(Spinner, { size: "lg" }) }));
    }
    if (error) {
        return _jsx(ErrorMessage, { children: error });
    }
    return (_jsxs(Container, { children: [_jsxs(Header, { children: [_jsx(Title, { children: "Prototypes" }), _jsxs(Button, { onClick: handleOpenModal, children: [_jsx(Plus, { size: 18 }), "Register Prototype"] })] }), prototypes.length === 0 ? (_jsxs(EmptyState, { children: [_jsx(EmptyStateTitle, { children: "No prototypes yet" }), _jsx(EmptyStateDescription, { children: "Register your first prototype to get started." }), _jsxs(Button, { onClick: handleOpenModal, children: [_jsx(Plus, { size: 18 }), "Register Prototype"] })] })) : (_jsx(PrototypeGrid, { children: prototypes.map((prototype) => (_jsx(PrototypeCard, { children: _jsxs(CardContent, { children: [_jsxs(PrototypeHeader, { children: [_jsx(PrototypeName, { children: prototype.name }), _jsxs(BadgesContainer, { children: [_jsx(StatusBadge, { status: getPrototypeStatusType(prototype.status), size: "sm", dot: true, children: prototype.status }), prototype.status === 'deployed' && prototype.lastHealthStatus && (_jsxs(HealthIndicator, { "$status": prototype.lastHealthStatus, children: [_jsx(Heart, { size: 12 }), prototype.lastHealthStatus] }))] })] }), prototype.description && _jsx(PrototypeDescription, { children: prototype.description }), _jsxs(PrototypeUrl, { href: prototype.githubRepoUrl, target: "_blank", rel: "noopener noreferrer", children: [_jsx(ExternalLink, { size: 12 }), prototype.githubRepoUrl] }), (prototype.status === 'building' || prototype.status === 'deploying' || prototype.status === 'failed') && (_jsx(PrototypeDeploymentProgress, { prototypeId: prototype.id })), _jsxs(PrototypeActions, { children: [prototype.status !== 'deployed' && (_jsxs(Button, { variant: "secondary", size: "sm", onClick: () => handleDeploy(prototype), disabled: deployingId === prototype.id, loading: deployingId === prototype.id, children: [_jsx(Play, { size: 14 }), "Deploy"] })), prototype.deploymentUrl && (_jsxs(ViewLink, { href: prototype.deploymentUrl, target: "_blank", rel: "noopener noreferrer", children: [_jsx(ExternalLink, { size: 14 }), "View"] })), _jsxs(Button, { variant: "danger", size: "sm", onClick: () => handleOpenTearDown(prototype), disabled: deletingId === prototype.id, loading: deletingId === prototype.id, children: [_jsx(Trash2, { size: 14 }), "Delete & Tear Down"] })] })] }) }, prototype.id))) })), _jsx(Modal, { isOpen: isModalOpen, onClose: handleCloseModal, title: modalStep === 'results' && validationResult
                    ? validationResult.isValid
                        ? 'Register Prototype'
                        : 'Validation Failed'
                    : 'Validate Repository', footer: renderModalFooter(), children: renderModalContent() }), _jsx(Modal, { isOpen: tearDownTarget !== null, onClose: handleCloseTearDown, title: "Delete & Tear Down", footer: _jsxs(_Fragment, { children: [_jsx(Button, { variant: "ghost", onClick: handleCloseTearDown, disabled: deletingId !== null, children: "Cancel" }), _jsxs(Button, { variant: "danger", onClick: handleConfirmTearDown, disabled: deletingId !== null ||
                                !tearDownTarget ||
                                tearDownConfirmText.trim() !== tearDownTarget.name, loading: deletingId !== null, children: [_jsx(Trash2, { size: 14 }), "Delete & Tear Down"] })] }), children: tearDownTarget && (_jsxs(_Fragment, { children: [_jsxs(TearDownWarning, { children: [_jsxs("strong", { children: ["This permanently destroys AWS resources for \"", tearDownTarget.name, "\"."] }), ' ', "The action cannot be undone."] }), _jsx("p", { style: { marginTop: theme.spacing.md, fontSize: theme.typography.fontSize.sm }, children: "The following will be destroyed:" }), _jsxs(TearDownList, { children: [_jsx("li", { children: "ECS service and any running tasks" }), _jsx("li", { children: "ALB target group and listener rule" }), _jsx("li", { children: "ECR repository and all container images" }), _jsx("li", { children: "EFS access point and stored data" }), _jsx("li", { children: "S3 seed database object" }), _jsx("li", { children: "Cloudflare DNS record" }), _jsx("li", { children: "The prototype record and all access grants" })] }), _jsxs(ConfirmInputLabel, { htmlFor: "teardown-confirm-input", children: ["To confirm, type the prototype name ", _jsx("strong", { children: tearDownTarget.name }), ":"] }), _jsx(Input, { id: "teardown-confirm-input", value: tearDownConfirmText, onChange: (e) => setTearDownConfirmText(e.target.value), placeholder: tearDownTarget.name, autoFocus: true }), tearDownError && (_jsx(ErrorMessage, { style: { marginTop: theme.spacing.sm }, children: tearDownError }))] })) })] }));
}
